import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { type RootState, type Store, select } from '@swf/core/store';

import type { DepositResult, FDMS3DS2Info } from '../../../../../../../../types/Cashier';
import { DDCStatus } from '../../RejectedX3dSecure2RequiredError';
import DDCStatusMessages from '../DDCStatusMessages/DDCStatusMessages';

import { useDepositResultPolling } from './useDepositResultPolling';

const Fdms: React.FC<DepositResult & { updateFdmsStatus: () => void }> = ({
  resultInfo,
  transactionId,
  updateFdmsStatus
}) => {
  const [resultDDCStatus, setResultDDCStatus] = useState<DDCStatus>();
  const [appended, setAppended] = useState(false);
  const jwt = resultInfo as FDMS3DS2Info;
  const { data } = useWizardContext();
  const request = useRequest();
  const { jumpForward } = useWizardJump();
  const wc2Domain = useSelector(select.location.getWC2Domain);
  const store = useStore<RootState>() as Store;

  const appendScript = useCallback(() => {
    const secureTradingScript = document.createElement('div');

    secureTradingScript.innerHTML = jwt;
    document.body.appendChild(secureTradingScript);
  }, [jwt]);

  const { startPolling } = useDepositResultPolling({
    request,
    transactionId,
    wc2Domain,
    store,
    onSuccess: (result) => {
      data.depositResult = result;
      updateFdmsStatus();
    },
    onFailure: () => {
      setResultDDCStatus(DDCStatus.ddcTimeout);
    }
  });

  useEffect(() => {
    if (!jwt || !transactionId) {
      setResultDDCStatus(DDCStatus.ddcError);
    } else {
      // TODO: TBC if ddcScript and fdmsNotify3dFormCompleted still needed since PYR-150755 bypassing DDC stage
      if (!appended) {
        setResultDDCStatus(DDCStatus.ddcOk);
        appendScript();
        setAppended(true);
      } else {
        request({
          service: 'wc3.paysystem.fdmsNotify3dFormCompleted',
          body: { transactionId }
        });

        startPolling();
      }
    }
  }, [
    appendScript,
    appended,
    data,
    jumpForward,
    jwt,
    request,
    store,
    transactionId,
    updateFdmsStatus,
    wc2Domain,
    startPolling
  ]);

  return <DDCStatusMessages ddcStatus={resultDDCStatus} />;
};

export default Fdms;
